import { getRuntimeConfig } from '@/config/runtimeConfig';

import type { Role } from './useRoles';

/**
 * The named synthetic users the Dev Login picker exposes. Keep in sync with the BE
 * `DevLoginIdentities.All` catalog — IDs, oids, and emails are the contract. Distinct
 * oids let us simulate "Originator A vs Originator B" scenarios on Dev (e.g.,
 * workflow-lock UX); the emails must match BE exactly since `RequireAuthorEmail`
 * attributes saved records using the `email` claim, not the oid.
 */
export type DevIdentity = {
  id: string;
  oid: string;
  name: string;
  role: Role;
  /** Mirrors BE `DevLoginIdentity.Email` — the handler emits this as the `email`
   *  claim, which `RequireAuthorEmail` prefers over `preferred_username` when
   *  attributing saved custom elements. Must stay in sync so the FE's "mine vs
   *  everyone else's" split matches what the BE actually persisted as `Author`.
   *
   *  The BE catalog alternates between two real mailboxes, so several identities
   *  share an address on purpose. That is faithful, not drift: authorship on Dev
   *  really is per-EMAIL, not per-identity, so Alice/Carol/Eve all see each other's
   *  custom elements as "mine". Switch the oid, not the email, to split them. */
  email: string;
};

export const DEV_IDENTITIES: ReadonlyArray<DevIdentity> = [
  {
    id: 'originator-1',
    oid: 'dev-originator-1',
    name: 'Alice Originator',
    role: 'Originator',
    email: 'martha.umar@paralucent.com',
  },
  {
    id: 'originator-2',
    oid: 'dev-originator-2',
    name: 'Bob Originator',
    role: 'Originator',
    email: 'yinlintao@gmail.com',
  },
  {
    id: 'approver-1',
    oid: 'dev-approver-1',
    name: 'Carol Approver',
    role: 'Approver',
    email: 'martha.umar@paralucent.com',
  },
  {
    id: 'approver-2',
    oid: 'dev-approver-2',
    name: 'Dan Approver',
    role: 'Approver',
    email: 'yinlintao@gmail.com',
  },
  {
    id: 'advisor-1',
    oid: 'dev-advisor-1',
    name: 'Eve Advisor',
    role: 'Advisor',
    email: 'martha.umar@paralucent.com',
  },
  {
    id: 'advisor-2',
    oid: 'dev-advisor-2',
    name: 'Frank Advisor',
    role: 'Advisor',
    email: 'yinlintao@gmail.com',
  },
];

const STORAGE_KEY = 'bmo-inflow.devIdentityId';

/** Stashes the originally-requested route across the dev-login redirect. */
export const INTENDED_ROUTE_KEY = 'bmo-inflow.intendedRoute';

/**
 * Validates a stashed intended route before navigating to it after dev login.
 * Must be a relative path; blocks open-redirect and auth-flow bounce-backs.
 */
export function safeIntendedRoute(raw: string | null): string | null {
  if (!raw) return null;
  if (!raw.startsWith('/')) return null;
  if (raw.startsWith('//') || raw.startsWith('/\\')) return null;
  if (raw === '/' || raw.startsWith('/auth/')) return null;
  return raw;
}

/** True when the running environment enables the Dev Login mock (localhost + Dev only). */
export function isDevLoginEnabled(): boolean {
  try {
    return getRuntimeConfig().auth?.devLogin === true;
  } catch {
    // Runtime config not loaded yet (e.g. unit tests) → treat as disabled.
    return false;
  }
}

/** The identity the user picked on the Dev Login screen, or null if none chosen. */
export function getDevIdentity(): DevIdentity | null {
  const v = sessionStorage.getItem(STORAGE_KEY);
  return DEV_IDENTITIES.find((i) => i.id === v) ?? null;
}

/** Shorthand — the role of the picked identity, for places that only need the role. */
export function getDevRole(): Role | null {
  return getDevIdentity()?.role ?? null;
}

export function setDevIdentity(id: string): void {
  sessionStorage.setItem(STORAGE_KEY, id);
}

export function clearDevIdentity(): void {
  sessionStorage.removeItem(STORAGE_KEY);
}

/**
 * Look an oid up in the dev identity catalog and return the matching display name.
 * Used as a fallback when a workflow's persisted `ownerName` is empty (e.g., seeded
 * before the field existed). Returns null when the oid isn't a known dev synthetic
 * so real SSO behavior in QA/Prod stays unaffected.
 */
export function devNameForOid(oid: string | null | undefined): string | null {
  if (!oid) return null;
  return DEV_IDENTITIES.find((i) => i.oid === oid)?.name ?? null;
}
